import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Checkbox, IconButton, Snackbar, Alert, Skeleton,
} from "@mui/material";
import { MdDelete, MdVisibility } from "react-icons/md";

const ADJUSTMENT_TYPES = {
  damage: { label: "Damage", color: "bg-red-100 text-red-800" },
  loss: { label: "Loss", color: "bg-red-100 text-red-800" },
  correction: { label: "Correction", color: "bg-blue-100 text-blue-800" },
  write_off: { label: "Write Off", color: "bg-gray-100 text-gray-800" },
  found: { label: "Found", color: "bg-green-100 text-green-800" },
  return: { label: "Return", color: "bg-indigo-100 text-indigo-800" },
};

const ADJUSTMENT_STATUSES = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
};

const Adjustments = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchAdjustments = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllStockAdjustments?page=${page + 1}&limit=${rowsPerPage}`;
      if (typeFilter) url += `&adjustmentType=${typeFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setAdjustments(data.adjustments || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch adjustments", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdjustments();
  }, [page, rowsPerPage, typeFilter]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(adjustments.map((a) => a._id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selected.length === 0) return;
    try {
      const response = await fetch("/api/deleteStockAdjustment", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount || selected.length} adjustment(s) deleted`, severity: "success" });
        setSelected([]);
        fetchAdjustments();
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to delete", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  const getTypeBadge = (type) => {
    const config = ADJUSTMENT_TYPES[type] || { label: type, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = ADJUSTMENT_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <h2 className="text-xl font-semibold text-gray-800">Stock Adjustments</h2>
        <button
          onClick={() => navigate("/Adjustments/New")}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          + New Adjustment
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          {Object.entries(ADJUSTMENT_TYPES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        {selected.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600"
          >
            <MdDelete /> Delete ({selected.length})
          </button>
        )}
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < adjustments.length}
                    checked={adjustments.length > 0 && selected.length === adjustments.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Adj #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Qty Change</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Previous</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>New</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(11)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : adjustments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No stock adjustments found</p>
                  </TableCell>
                </TableRow>
              ) : (
                adjustments.map((adj) => (
                  <TableRow
                    key={adj._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/Adjustments/View/${adj._id}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(adj._id)}
                        onChange={() => handleSelect(adj._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-primary">
                        {adj.adjustmentNumber || adj._id?.slice(-6).toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>
                      {adj.item?.name || "N/A"}
                    </TableCell>
                    <TableCell>
                      {getTypeBadge(adj.adjustmentType)}
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${adj.quantityChange > 0 ? "text-green-600" : "text-red-600"}`}>
                        {adj.quantityChange > 0 ? "+" : ""}{adj.quantityChange}
                      </span>
                    </TableCell>
                    <TableCell>
                      {adj.previousQuantity ?? "-"}
                    </TableCell>
                    <TableCell>
                      {adj.newQuantity ?? "-"}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 truncate block max-w-[200px]">
                        {adj.reason || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {adj.date
                        ? new Date(adj.date).toLocaleDateString()
                        : adj.createdAt
                        ? new Date(adj.createdAt).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(adj.status)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/Adjustments/View/${adj._id}`)}
                      >
                        <MdVisibility className="text-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Adjustments;
